<template>
  <ItemPage
    :title="`Questão #${this.$route.params.id}`"
    size="md"
    :actions="actions"
  >
    <div>
      <div
        v-if="question"
      >
        <div v-if="question.intranet_user">
          Criada por {{ question.intranet_user.name }} {{ dayjs(question.created_at).fromNow() }}
        </div>
        <div
          v-if="corrupted"
          class="mt-6 red--text"
        >
          Conteúdo corrompido! Apague a questão e cadastre-a novamente.
        </div>
        <v-row class="mt-6 align-center">
          <v-col md="6">
            <v-select
              v-model="question.question_board_id"
              :items="questionBoards"
              item-text="name"
              item-value="id"
              label="Banca"
              outlined
              hide-details="auto"
              :disabled="busy"
              @change="addQuestionBoardToQuestion"
            />
          </v-col>
          <v-col
            v-if="question.question_board_id"
            cols="auto"
          >
            <v-btn
              color="grey darken-2"
              small
              text
              :disabled="busy"
              @click="removeQuestionBoardFromQuestion()"
            >
              <v-icon
                small
                left
              >
                mdi-close
              </v-icon>
              Remover Banca
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          class="mt-6"
          align="center"
          no-gutters
        >
          <v-col
            cols="12"
            sm="6"
          >
            <div class="font-weight-bold">
              Tópicos
            </div>
            <div class="text-body-2 text--secondary mt-1">
              Adicione tópicos para classificar esta questão na hierarquia Aude.
            </div>
          </v-col>

          <v-col
            class="ml-auto"
            cols="auto"
          >
            <v-btn
              color="primary"
              text
              small
              @click="openAddUnitDialog"
            >
              <v-icon
                small
                left
              >
                mdi-plus
              </v-icon> Adicionar Tópico
            </v-btn>
          </v-col>
        </v-row>

        <v-simple-table
          v-if="question.units.length"
          class="mt-2"
        >
          <tbody>
            <tr
              v-for="unit in question.units"
              :key="unit.id"
            >
              <td class="py-2">
                <div>{{ unit.name }} - {{ getDifficultyName(unit.pivot.difficulty) }}</div>
                <div class="grey--text text--darken-1">
                  {{ unit.grade_subject.name }} - {{ unit.grade.name }} - {{ unit.education_stage.name }} - {{ unit.education_system.name}}
                </div>
              </td>
              <td class="text-right">
                <v-btn
                  color="grey darken-1"
                  small
                  text
                  @click="removeUnitFromQuestion(unit.id)"
                >
                  <v-icon
                    small
                    left
                  >
                    mdi-delete
                  </v-icon>
                  Remover
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div
          v-else
          class="grey--text my-4 text-center"
        >
          Esta questão ainda não possui Tópicos, adicione o primeiro!
        </div>
        <v-row
          class="mt-6"
          align="center"
          no-gutters
        >
          <v-col
            cols="12"
            sm="6"
          >
            <div class="font-weight-bold">
              Tags
            </div>
            <div class="text-body-2 text--secondary mt-1">
              As tags serão utilizadas para relacionar conteúdos com características em comum.
            </div>
          </v-col>

          <v-col
            class="ml-auto"
            cols="auto"
          >
            <v-btn
              color="primary"
              text
              small
              @click="openAddTagDialog"
            >
              <v-icon
                small
                left
              >
                mdi-plus
              </v-icon> Adicionar Tag
            </v-btn>
          </v-col>
        </v-row>

        <v-simple-table
          v-if="question.tags.length"
          class="mt-2"
        >
          <tbody>
            <tr
              v-for="tag in question.tags"
              :key="tag.id"
            >
              <td class="py-2">
                <div>{{ tag.name }}</div>
                <div class="grey--text text--darken-1">
                  {{ tag.tag_type.name }}
                </div>
              </td>
              <td class="text-right">
                <v-btn
                  color="grey darken-2"
                  small
                  text
                  @click="removeTagFromQuestion(tag.id)"
                >
                  <v-icon
                    small
                    left
                  >
                    mdi-delete
                  </v-icon>
                  Remover
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <div
          v-else
          class="grey--text my-4 text-center"
        >
          Esta questão ainda não possui Tags, adicione a primeira!
        </div>
      </div>

      <v-divider />

      <div
        v-if="!corrupted"
        class="pa-8"
      >
        <hr>
        <div class="d-flex align-center justify-space-between my-4">
          <div class="title">
            Conteúdo
          </div>

          <v-btn
            color="blue darken-2"
            small
            text
            @click="onEditClick()"
          >
            <v-icon
              small
              left
            >
              mdi-pencil
            </v-icon>
            Editar Conteúdo
          </v-btn>
        </div>

        <div class="font-weight-bold">
          Enunciado
        </div>

        <div class="editor elevation-1 mt-3">
          <div>
            <editor-content
              v-if="statementEditor"
              class="editor-content pa-5"
              :editor="statementEditor"
            />
          </div>
        </div>

        <div class="mt-12 d-flex align-center">
          <div class="font-weight-bold">
            Respostas
          </div>
        </div>

        <template v-if="answers.length > 0">
          <div
            v-for="(answer, index) in answers"
            :key="answer.id"
            class="mt-8"
          >
            <div class="d-flex justify-space-between">
              <div>Resposta {{ index + 1 }}</div>

              <div
                v-if="correctAnswerId === answer.id"
                class="success--text"
                style="min-height: 36px;"
              >
                <v-icon color="success">
                  mdi-check
                </v-icon>
                Correta
              </div>
            </div>
            <div class="mt-2 editor elevation-1">
              <div>
                <editor-content
                  v-if="answer.editor"
                  class="editor-content pa-5"
                  :editor="answer.editor"
                />
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          class="grey--text text-center my-5"
        >
          Esta questão ainda não possui respostas.
        </div>
      </div>
    </div>
  </ItemPage>
</template>

<script>
import utils from '@/api/utils.js'
import ItemPage from '@/components/pages/ItemPage.vue'
import AddTagDialog from '@/components/contents/questions/AddTagDialog.vue'
import { Editor, EditorContent } from 'tiptap'
import {
  HardBreak,
  Bold,
  Italic,
  History
} from 'tiptap-extensions'

import { getTextForDisplay as getDifficultyName } from '@/enums/questionDifficultyType'
import Image from '@/components/contents/questions/editor/nodes/Image'
import Katex from '@/components/contents/questions/editor/nodes/Katex'
import { makeid } from '../../../../util'
import dayjs from '@/dayjs'
import AddUnitDialog from '@/components/contents/questions/AddUnitDialog.vue'

export default {
  components: {
    EditorContent,
    ItemPage
  },

  data: () => ({
    question: null,
    questionBoards: [],
    statementEditor: null,
    answers: [],
    corrupted: false,
    correctAnswerId: null,
    busy: false,
    user: {},
    questionsUpdate: true,
    questionsDelete: true
  }),

  computed: {
    isPublished () {
      if (!this.question) {
        return false
      }

      return this.question.is_draft === 0
    },

    actions () {
      return [
        {
          label: 'Excluir',
          action: this.deleteQuestion,
          icon: 'mdi-delete',
          disabled: false
        }
      ]
    }
  },

  async mounted () {
    await this.getQuestionBoards()
    await this.fetchData()
    this.validate()
  },

  beforeDestroy () {
    this.statementEditor.destroy()
    for (const answer of this.answers) {
      answer.editor.destroy()
    }
  },

  methods: {
    async getQuestionBoards () {
      const data = await this.$api.getQuestionBoards()
      this.questionBoards = data
    },
    async fetchData () {
      this.answers = []
      this.question = null
      this.statementEditor = null
      if (!this.$route.params.id) {
        return
      }
      this.busy = true
      const res = await this.$api.getQuestion(this.$route.params.id)
      this.question = res

      const textData = JSON.parse(res.text_data)

      this.statementEditor = new Editor({
        editable: false,
        extensions: [
          new HardBreak(),
          new Image(),
          new Bold(),
          new Italic(),
          new History(),
          new Katex()
        ],
        content: {
          type: 'doc',
          content: [
            {
              type: 'paragraph'
            }
          ]
        }
      })

      try {
        this.statementEditor.setContent(textData.statement, true)
      } catch (e) {
        this.corrupted = true
      }

      this.correctAnswerId = textData.correctAnswerId

      for (const answer of textData.answers) {
        try {
          answer.editor = new Editor({
            editable: false,
            extensions: [
              new HardBreak(),
              new Image(),
              new Bold(),
              new Italic(),
              new History(),
              new Katex()
            ],
            content: answer.state
          })
        } catch (e) {
          this.corrupted = true
        }

        answer.isCorrect = textData.correnctAnswerId && (textData.correnctAnswerId === answer.id)
        delete answer.state

        if (!answer.id) {
          answer.id = makeid()
        }

        this.answers.push(answer)
      }

      this.busy = false
    },

    validate () {
      this.user = utils.getUser()
      this.questionsUpdate = utils.checkAnyOrOwn('questions.update-any', 'questions.update-own', this.question.intranet_user_id, this.user.id)
      this.questionsDelete = utils.checkAnyOrOwn('questions.delete-any', 'questions.delete-own', this.question.intranet_user_id, this.user.id)
    },

    onEditClick () {
      this.$router.push({ name: 'conteudos.questoes.editar.id', params: { id: this.$route.params.id } })
    },

    async deleteQuestion () {
      this.$dialog.confirm('Apagar questão?')
        .text('Essa ação não pode ser desfeita')
        .label('Apagar')
        .onConfirm(async () => {
          try {
            await this.$api.deleteQuestion(this.$route.params.id)
            this.$store.dispatch('setSnackBar', { text: 'Questão Apagada com sucesso.' })
            this.$router.push({ name: 'conteudos.questoes' })
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível Excluir, tente novamente mais tarde.', color: 'red' })
          }
        })
        .show()
    },

    openAddUnitDialog () {
      this.$dialog.custom(AddUnitDialog).show()
        .then(async (unit) => {
          if (!unit) {
            return
          }
          await this.addUnitToQuestion(unit)
        })
    },
    async addUnitToQuestion (unitId) {
      try {
        await this.$api.addUnitToQuestion(this.question.id, unitId)
        await this.fetchData()
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Não foi possível adicionar o tópico, tente novamente mais tarde.', color: 'red' })
      }
    },
    async removeUnitFromQuestion (unitId) {
      this.$dialog.confirm('Apagar Tópico?')
        .text('Tem certeza que deseja remover este tópico da questão? Essa ação não pode ser desfeita.')
        .label('Apagar')
        .onConfirm(async () => {
          try {
            await this.$api.removeUnitFromQuestion(this.question.id, unitId)
            await this.fetchData()
            this.$store.dispatch('setSnackBar', { text: 'Tópico removido com sucesso.' })
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível remover o tópico, tente novamente mais tarde.', color: 'red' })
          }
        })
        .show()
    },

    openAddTagDialog () {
      this.$dialog.custom(AddTagDialog).show()
        .then(async (tag) => {
          if (!tag) {
            return
          }
          await this.addTagToQuestion(tag)
        })
    },
    async addTagToQuestion (tagId) {
      try {
        await this.$api.addTagToQuestion(this.question.id, tagId)
        await this.fetchData()
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Não foi possível adicionar a tag, tente novamente mais tarde.', color: 'red' })
      }
    },
    async removeTagFromQuestion (tagId) {
      this.$dialog.confirm('Apagar Tag?')
        .text('Tem certeza que deseja remover esta tag da questão? Essa ação não pode ser desfeita.')
        .label('Apagar')
        .onConfirm(async () => {
          try {
            await this.$api.removeTagFromQuestion(this.question.id, tagId)
            await this.fetchData()
            this.$store.dispatch('setSnackBar', { text: 'Tag removida com sucesso.' })
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível remover a tag, tente novamente mais tarde.', color: 'red' })
          }
        })
        .show()
    },

    async addQuestionBoardToQuestion (questionBoardId) {
      const data = {
        question_board_id: questionBoardId
      }
      try {
        await this.$api.addQuestionBoardToQuestion(this.question.id, data)
        await this.fetchData()
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Não foi possível adicionar banca à questão, tente novamente mais tarde.', color: 'red' })
      }
    },
    async removeQuestionBoardFromQuestion () {
      this.$dialog.confirm('Apagar Banca?')
        .text('Tem certeza que deseja remover esta banca da questão? Essa ação não pode ser desfeita.')
        .label('Apagar')
        .onConfirm(async () => {
          try {
            await this.$api.removeQuestionBoardFromQuestion(this.question.id)
            await this.fetchData()
            this.$store.dispatch('setSnackBar', { text: 'Banca removida com sucesso.' })
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível remover a banca, tente novamente mais tarde.', color: 'red' })
          }
        })
        .show()
    },

    getDifficultyName,
    dayjs
  }
}
</script>
